/**
 * AdMob Rewarded Ad Module
 */

import { AdMob, RewardAdPluginEvents } from '@capacitor-community/admob';
import { Capacitor } from '@capacitor/core';

const AD_UNIT_IDS = {
  android: 'ca-app-pub-5244313691595903/6748736868',
  ios: 'ca-app-pub-3940256099942544/1712485313', // Test (iOS not yet set up)
};

let isAdLoaded = false;
let isAdLoading = false;
let rewardEarned = false;
let resolveShow: ((earned: boolean) => void) | null = null;
let listenersRegistered = false;

function ensureListeners(): void {
  if (listenersRegistered) return;
  listenersRegistered = true;

  AdMob.addListener(RewardAdPluginEvents.Loaded, () => {
    console.log('[AdMob] Rewarded ad loaded');
    isAdLoaded = true;
    isAdLoading = false;
  });

  AdMob.addListener(RewardAdPluginEvents.FailedToLoad, (error) => {
    console.error('[AdMob] Rewarded ad failed to load', error);
    isAdLoaded = false;
    isAdLoading = false;
  });

  AdMob.addListener(RewardAdPluginEvents.Rewarded, () => {
    console.log('[AdMob] User earned reward');
    rewardEarned = true;
  });

  AdMob.addListener(RewardAdPluginEvents.Dismissed, () => {
    console.log('[AdMob] Rewarded ad dismissed');
    isAdLoaded = false;
    if (resolveShow) {
      resolveShow(rewardEarned);
      resolveShow = null;
    }
    rewardEarned = false;
    preloadRewarded();
  });

  AdMob.addListener(RewardAdPluginEvents.FailedToShow, (error) => {
    console.error('[AdMob] Rewarded ad failed to show', error);
    isAdLoaded = false;
    if (resolveShow) {
      resolveShow(false);
      resolveShow = null;
    }
  });
}

export async function preloadRewarded(): Promise<void> {
  if (!Capacitor.isNativePlatform() || isAdLoading || isAdLoaded) return;

  isAdLoading = true;
  const platform = Capacitor.getPlatform();
  const adId = platform === 'android' ? AD_UNIT_IDS.android : AD_UNIT_IDS.ios;

  try {
    ensureListeners();
    await AdMob.prepareRewardVideoAd({ adId });
    console.log('[AdMob] Rewarded ad preloaded');
  } catch (error) {
    console.error('[AdMob] Failed to preload rewarded ad', error);
    isAdLoading = false;
  }
}

/**
 * Show rewarded ad
 * @returns true if reward was earned
 */
export async function showRewarded(): Promise<boolean> {
  if (!Capacitor.isNativePlatform()) {
    console.log('[AdMob] Not native platform, simulating reward');
    return true; // Always succeed in dev environment
  }

  if (!isAdLoaded) {
    console.log('[AdMob] Rewarded ad not loaded, attempting load');
    await preloadRewarded();
    // Wait for load to complete
    await new Promise((r) => setTimeout(r, 2000));
    if (!isAdLoaded) return false;
  }

  return new Promise((resolve) => {
    resolveShow = resolve;
    rewardEarned = false;
    AdMob.showRewardVideoAd().catch((error) => {
      console.error('[AdMob] Failed to show rewarded ad', error);
      resolve(false);
    });
  });
}
